import * as d3 from 'd3';
import { startOfDay, endOfDay } from 'date-fns';
import { isEqual } from 'lodash';
import { Subject, Observable } from 'rxjs';

import { ElementRef, Optional } from '@angular/core';

import { SnDVRStream } from '@lib/api/entities';

import { ITimelineDefinition, ITimeline } from '../interfaces';

const MARKER_HIT_AREA_WIDTH = 16;
const MARKER_HIT_AREA_OFFSET = MARKER_HIT_AREA_WIDTH / 2;

interface TooltipHost {
  updateTooltip(event: MouseEvent, timeText: string): void;
  hideTooltip(): void;
}

export class Timeline {
  get segment$(): Observable<ITimeline> {
    return this._segment$.asObservable();
  }

  get brush$(): Observable<[Date, Date]> {
    return this._brush$.asObservable();
  }

  get moveCursor$(): Observable<Date> {
    return this._moveCursor$.asObservable();
  }

  get dragging$(): Observable<boolean> {
    return this._dragging$.asObservable();
  }

  get zoom$(): Observable<d3.ZoomTransform> {
    return this._zoom$.asObservable();
  }

  get userSeeking$(): Observable<boolean> {
    return this._userSeeking$.asObservable();
  }

  set fullscreen(value: boolean) {
    this._fullscreen = value;
  }

  set data(value: any) {
    const hasChanged = !isEqual(this._data, value);
    this._data = value;

    if (hasChanged && this._progressElement) {
      this._update();
    }
  }

  set currentDate(value: Date) {
    this._currentDate = value;
  }

  set currentTime(value: Date) {
    this._currentTime = value;
    if (value > this._liveTime) {
      this._liveTime = value;
    }
    this._updateMarkerPosition();
  }

  set currentThumbnail(camera: SnDVRStream) {
    this._preUrl = camera;
  }

  set height(value: number) {
    this._height = value;
  }

  public transformEvent;
  private _brushRange: [Date, Date];
  private _height = 96;
  private _disableThumnbail: boolean;
  private _currentDate: Date;
  private _currentTime: Date = new Date();
  private _liveTime: Date = new Date();
  private _data: any;
  private _marker;
  private _hoverMarker;
  private _markerHitArea;
  private _timeTooltip;
  private _activeSegment;
  private _thumbActive;

  private _svg: d3.Selection<SVGSVGElement, ITimelineDefinition, null, undefined>;
  private _xScale: d3.ScaleTime<number, number>;
  private _axis: d3.Axis<Date>;
  private _timelineContainer: d3.Selection<SVGGElement, ITimelineDefinition, null, undefined>;
  private _progressElement: d3.Selection<SVGGElement, unknown, SVGGElement, ITimelineDefinition>;

  private _xBaseScale: d3.ScaleTime<number, number>;
  private readonly _brush: d3.BrushBehavior<null>;
  private readonly _zoom: d3.ZoomBehavior<Element, any>;
  private readonly _drag: d3.DragBehavior<Element, unknown, unknown>;

  private _width: number;
  private readonly _timelineHeightFn = this._calcTimelineHeight.bind(this);
  private readonly _timelineOffsetYFn = this._calcTimelineOffsetY.bind(this);

  private readonly _brush$ = new Subject<[Date, Date]>();
  private readonly _dragging$ = new Subject<boolean>();
  private readonly _moveCursor$ = new Subject<Date>();
  private readonly _segment$ = new Subject<ITimeline>();
  private readonly _zoom$ = new Subject<d3.ZoomTransform>();
  private readonly _userSeeking$ = new Subject<boolean>();
  private _thumbnailTimeout;

  private _preUrl: SnDVRStream;

  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    date: Date,
    data: ITimelineDefinition[],
    @Optional() height?: number,
    @Optional() disableThumbnail?: boolean,
    @Optional() private _fullscreen?: boolean,
    @Optional() private _tooltipHost?: TooltipHost,
  ) {
    this._width = this._elementRef.nativeElement.offsetWidth;
    this._height = height ? height : this._height;
    this._disableThumnbail = disableThumbnail;

    this._xBaseScale = d3.scaleTime().range([0, this._width]);
    this._brush = d3.brushX<null>().on('end', () => this._onBrush());
    this._zoom = d3.zoom<Element, ITimelineDefinition>().scaleExtent([1, 30]);

    this._drag = d3
      .drag()
      .on('start', () => this._onDragStart())
      .on('drag', () => this._onDrag())
      .on('end', () => this._onDragEnd());

    this.data = data;
    this.currentDate = date;

    this._draw();
  }

  public nextIfAvailable(): void {
    if (this._activeSegment) {
      this._activeSegment.attr('class', 'timeline--segment');
    }
    const end = parseInt(this._activeSegment?.attr('data-end'), 10);
    const next = this._data[0].timeline.find((timeline: ITimeline) => {
      return timeline.start > end;
    });
    if (!next) {
      return;
    }
    this._activateSegment(d3.select(`[data-start="${next.start}"]`));
    this.currentTime = new Date(next.start * 1000);
    this._moveCursor$.next(this._currentTime);
  }

  public deactivateSegment(): void {
    this._activeSegment?.attr('class', 'timeline--segment');
    this._activeSegment = null;
    this.currentTime = new Date();
  }

  public destroy(): void {
    this._brush$.complete();
    this._moveCursor$.complete();
    this._dragging$.complete();
    this._userSeeking$.complete();
    
    if (this._hoverMarker) {
      this._hoverMarker.remove();
    }
    
    if (this._timeTooltip) {
      this._timeTooltip.remove();
    }
    
    if (this._thumbActive) {
      this._thumbActive.remove();
    }
    
    d3.select<HTMLElement, ITimelineDefinition>(this._elementRef.nativeElement)
      .selectAll('svg')
      .remove();
  }

  public redraw(): void {
    d3.select<HTMLElement, ITimelineDefinition>(this._elementRef.nativeElement)
      .selectAll('svg')
      .remove();

    this._draw();
    this._reinitializeInteractionHandlers();
  }

  private _reinitializeInteractionHandlers(): void {
    this._userSeeking$.next(false);
    
    if (this._markerHitArea && this._drag) {
      this._markerHitArea.call(this._drag);
    }
  }

  public resetInteractionState(): void {
    this._reinitializeInteractionHandlers();
  }

  public updateTimeline(): void {
    this._update();
  }

  public resetTransform(): void {
    this.transformEvent = null;
  }

  public setZoom(transformEvent: d3.ZoomTransform): void {
    this._zoom$.next(transformEvent);
    this._xScale = transformEvent.rescaleX(this._xBaseScale);
    this._timelineContainer.call(this._axis.scale(this._xScale));
    this.transformEvent = transformEvent;

    this._timelineContainer
      .selectAll('.timeline--segment')
      .attr('x', (d: ITimeline) => transformEvent.applyX(this._getSegmentXPosition(d)))
      .attr('width', (d: ITimeline) => this._getSegmentWidth(d, transformEvent.k));

    this._activeSegment = this._timelineContainer.select('.timeline--segment--active');

    this._timelineContainer
      .select('.timeline--marker')
      .attr('x1', this._xScale(this._currentTime))
      .attr('x2', this._xScale(this._currentTime));

    this._hoverMarker.attr('y2', this._timelineHeightFn);

    this._timelineContainer
      .select('.timeline--marker-hit-area')
      .attr('x', this._xScale(this._currentTime) - MARKER_HIT_AREA_OFFSET);

    if (this._brushRange) {
      const selection = [this._xScale(this._brushRange[0]), this._xScale(this._brushRange[1])];
      this._resetBrush();
      this._svg.select('.brush').call(this._brush.move, selection, { emitEvent: false });
    }
  }

  private _draw(): void {
    this._width = this._elementRef.nativeElement.offsetWidth;
    this._xBaseScale = d3.scaleTime().range([0, this._width]);
    this._xBaseScale.domain([startOfDay(this._currentDate), endOfDay(this._currentDate)]);
    this._xScale = this._xBaseScale;

    this._svg = d3
      .select<HTMLElement, ITimelineDefinition>(this._elementRef.nativeElement)
      .append('svg')
      .attr('width', this._width)
      .attr('height', this._height)
      .attr('class', 'chart');

    this._initScale();
    this._initTimeline();
  }

  private _initScale(): void {
    this._axis = d3.axisTop<Date>(this._xBaseScale).tickFormat(d3.timeFormat('%H:%M'));
    this._zoom
      .translateExtent([
        [0, 0],
        [this._width, this._height],
      ])
      .extent([
        [0, 0],
        [this._width, this._height],
      ])
      .on('zoom', () => this._onZoom());

    this._svg.call(this._zoom);
  }

  private _initTimeline(): void {
    this._timelineContainer = this._svg
      .append('g')
      .attr('x', 0)
      .attr('transform', 'translate(0, 20)')
      .attr('width', this._width)
      .attr('class', 'timeline')
      .call(this._axis);

    this._progressElement = this._timelineContainer
      .selectAll('.timeline--progress')
      .data(this._data)
      .enter()
      .append('g')
      .attr('class', 'timeline--progress')
      .attr('transform', (d, index) => `translate(0, ${this._timelineOffsetYFn() * index})`);

    this._progressElement
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', this._width)
      .attr('height', this._timelineHeightFn);

    const timeline = this._data.map((item) => item.timeline)[0];
    if (timeline) {
      this._progressElement
        .selectAll('.timeline--segment')
        .data((data: any, pIndex) => {
          return data.timeline.map((item) => ({
            ...item,
            index: pIndex,
          }));
        })
        .enter()
        .append('rect')
        .attr('data-start', (d: any) => d.start)
        .attr('data-end', (d: any) => d.end)
        .attr('data-path', (d: any) => d.file_path)
        .attr('x', (d: any) => this._getSegmentXPosition(d))
        .attr('y', 0)
        .attr('width', (d: any) => this._getSegmentWidth(d))
        .attr('height', this._timelineHeightFn)
        .attr('class', 'timeline--segment')
        .on('click', () => this._onClick());
    } else {
      this._progressElement
        .selectAll('.timeline--segment')
        .data((data: any, pIndex) => {
          return data[0].record.map((item) => ({
            ...item,
            index: pIndex,
          }));
        })
        .enter()
        .append('rect')
        .attr('x', (d: any) => this._getSegmentXPosition(d))
        .attr('y', 0)
        .attr('width', (d: any) => this._getSegmentWidth(d))
        .attr('height', this._timelineHeightFn)
        .attr('class', 'timeline--segment')
        .on('click', () => this._onClick());
    }

    this._marker = this._timelineContainer
      .append('line')
      .attr('x1', this._xBaseScale(this._currentTime))
      .attr('y1', 0)
      .attr('x2', this._xBaseScale(this._currentTime))
      .attr('y2', this._timelineHeightFn)
      .style('stroke-width', 4)
      .style('stroke', 'white')
      .style('fill', 'none')
      .attr('class', 'timeline--marker');

    this._hoverMarker = this._timelineContainer
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', this._timelineHeightFn)
      .style('stroke-width', 4)
      .style('stroke', '#36393D')
      .style('fill', 'none')
      .style('opacity', 0)
      .attr('class', 'timeline--hover-marker')
      .style('pointer-events', 'none');

    this._markerHitArea = this._timelineContainer
      .append('rect')
      .attr('x', this._xBaseScale(this._currentTime) - MARKER_HIT_AREA_OFFSET)
      .attr('y', 0)
      .attr('width', MARKER_HIT_AREA_WIDTH)
      .attr('height', this._timelineHeightFn)
      .style('fill', 'transparent')
      .style('cursor', 'col-resize')
      .attr('class', 'timeline--marker-hit-area')
      .call(this._drag);

    this._timeTooltip = d3
      .select('body')
      .append('div')
      .style('position', 'absolute')
      .style('z-index', '2000')
      .style('visibility', 'hidden')
      .attr('class', 'timeline--tooltip')
      .attr('x', 0)
      .attr('y', 0);

    this._thumbActive = d3
      .select('body')
      .append('div')
      .style('visibility', 'hidden')
      .style('box-shadow', '0 0 5px black, 0 0 5px black, 0 0 5px black, 0 0 5px black')
      .style('visibility', 'hidden')
      .style('background-color', '#000')
      .style('border', '1px solid #fff')
      .style('z-index', '2000')
      .style('position', 'absolute')
      .style('width', '400px')
      .style('transform', 'translateX(-50px)');

    this._timelineContainer
      .on('mouseenter', () => this._onMouseEnter())
      .on('mousemove', () => this._onMouseMove())
      .on('mouseleave', () => this._onMouseLeave());
  }

  private _onClick(): void {
    this._userSeeking$.next(true);
    if (this._activeSegment) {
      this._activeSegment.attr('class', 'timeline--segment');
    }
    this._activateSegment(d3.select(d3.event.target));
    this._resetBrush();
    const x = d3.event.offsetX;
    this._marker.attr('x1', x);
    this._marker.attr('x2', x);

    if (this._markerHitArea) {
      this._markerHitArea.attr('x', x - MARKER_HIT_AREA_OFFSET);
    }

    const date = this._xScale.invert(this._marker.attr('x1'));
    this._currentTime = date;
    this._moveCursor$.next(this._currentTime);
  }

  private _onDragStart(): void {
    this._userSeeking$.next(true);
    this._dragging$.next(true);
    this._marker.style('stroke', 'rgba(54, 57, 61, 0.9)');
    this._hoverMarker.style('opacity', 0);
    const yPosition = document.querySelector('.timeline--progress').getBoundingClientRect().bottom;
    
    if (this._fullscreen && this._tooltipHost) {
      const currentTime = this._xScale.invert(this._marker.attr('x1'));
      const timeString = currentTime.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
      this._tooltipHost.updateTooltip(d3.event.sourceEvent, timeString);
    }
    
    if (this._disableThumnbail) {
      return;
    }
    this._timeTooltip
      .style('visibility', 'visible')
      .text(this._xScale.invert(this._marker.attr('x1')).toLocaleTimeString())
      .style('top', `${yPosition + window.scrollY + 10}px`)
      .style('left', `${d3.event.sourceEvent.pageX - 10}px`);

    this._thumbActive
      .style('visibility', 'visible')
      .style('left', `${d3.event.sourceEvent.pageX - 10}px`)
      .style('bottom', `${window.innerHeight - yPosition + +this._timelineHeightFn() + 10}px`);
  }

  private _onDragEnd(): void {
    this._dragging$.next(false);
    this._marker.style('stroke', 'white');
    const date = this._xScale.invert(this._marker.attr('x1'));
    this._currentTime = date;
    this._moveCursor$.next(this._currentTime);
    this._timeTooltip.style('visibility', 'hidden');
    this._thumbActive.style('visibility', 'hidden');
    
    if (this._fullscreen && this._tooltipHost) {
      this._tooltipHost.hideTooltip();
    }
  }

  private _onDrag(): void {
    if (!this._activeSegment || this._activeSegment?.empty()) {
      const timeline = this._data[0]?.timeline;
      if (timeline?.length) {
        const lastSegment = timeline[timeline.length - 1];
        const targetRects =
          this._elementRef.nativeElement.querySelectorAll('rect.timeline--segment');
        if (targetRects) {
          targetRects.forEach((rect) => {
            if (rect.attributes['data-start']?.value === lastSegment.start.toString()) {
              this._activateSegment(d3.select(rect));
            }
          });
        }
      }
    } else {
      this._updateCursor();
    }
  }

  private _updateCursor(): void {
    const newx = parseFloat(d3.event.x);
    const startOfSegment = parseFloat(this._activeSegment.attr('x'));
    const endOfSegment = startOfSegment + parseFloat(this._activeSegment.attr('width'));
    let setx = newx < startOfSegment ? startOfSegment : newx;
    setx = setx > endOfSegment ? endOfSegment : setx;
    this._marker.attr('x1', setx);
    this._marker.attr('x2', setx);

    if (this._markerHitArea) {
      this._markerHitArea.attr('x', setx - MARKER_HIT_AREA_OFFSET);
    }

    this._timeTooltip.text(this._xScale.invert(setx).toLocaleTimeString());
    const date = this._xScale.invert(this._marker.attr('x1'));
    this._currentTime = date;
    this._moveCursor$.next(this._currentTime);
    
    if (this._fullscreen && this._tooltipHost) {
      const timeString = date.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
      this._tooltipHost.updateTooltip(d3.event.sourceEvent, timeString);
    }
    if (setx === newx) {
      clearTimeout(this._thumbnailTimeout);
      d3.select('#thumb-v').remove();

      this._timeTooltip.style('left', `${d3.event.sourceEvent.pageX - 10}px`);

      const currentTime = new Date(this._xScale.invert(setx)).getTime();
      const thumbDate = Math.floor(currentTime / 1000);

      const elmnRef = this._thumbActive
        .style('left', `${d3.event.sourceEvent.pageX - 10}px`)
        .append('video')
        .attr('id', 'thumb-v')
        .attr('width', '100%')
        .attr('height', '100%');

      if (this._disableThumnbail) {
        return;
      }
      this._thumbnailTimeout = setTimeout(() => {
        elmnRef.attr('src', this._preUrl.getMp4Thumbnails(thumbDate));
      }, 150);
    }
  }

  private _onBrush(): void {
    if (d3.event.sourceEvent.type === 'zoom') {
      return;
    }

    this._brushRange = d3.event.selection
      ? d3.event.selection.map((position) => this._xScale.invert(position))
      : null;

    this._brush$.next(this._brushRange);
  }

  private _onZoom(): void {
    const transformEvent: d3.ZoomTransform = d3.event.transform;
    this.setZoom(transformEvent);
  }

  private _resetBrush(): void {
    if (!this._activeSegment) {
      return;
    }
    this._svg.select('.brush').remove();
    const startOfSegment = parseFloat(this._activeSegment.attr('x'));
    const endOfSegment = startOfSegment + parseFloat(this._activeSegment.attr('width'));
    this._brush.extent([
      [startOfSegment, 2],
      [endOfSegment, this._height],
    ]);
    this._svg
      .append('g')
      .lower()
      .attr('class', 'x brush')
      .call(this._brush)
      .selectAll('rect')
      .attr('y', 5)
      .attr('height', this._height);

    this._timelineContainer.select('.axis').lower();
  }

  private _activateSegment(segment: Object): void {
    this._activeSegment = segment;
    this._activeSegment.attr('class', 'timeline--segment timeline--segment--active');
    const startOfSegment = parseFloat(this._activeSegment.attr('x'));
    const endOfSegment = startOfSegment + parseFloat(this._activeSegment.attr('width'));
    this._segment$.next({
      path: this._activeSegment.attr('data-path'),
      start: this._xScale.invert(startOfSegment).getTime() / 1000,
      end: this._xScale.invert(endOfSegment).getTime() / 1000,
    });
  }

  private _prepareSegmentData(): any[] {
    const timeline = this._data.map((item) => item.timeline)[0];

    if (timeline) {
      return timeline.map((item, index) => ({
        ...item,
        id: `${item.start}-${item.end}`,
        index,
      }));
    } else {
      return this._data[0].record.map((item, index) => ({
        ...item,
        id: `${item.start}-${item.end}`,
        index,
      }));
    }
  }

  private _getSegmentXPosition(d: ITimeline | any): number {
    const isIsoString = typeof d.start === 'string' ? true : false;
    const date = !isIsoString ? new Date(d.start * 1000) : new Date(d.start);
    return this._xBaseScale(date);
  }

  private _getSegmentWidth(d: ITimeline | any, k: number = 1): number {
    const isIsoString = typeof d.start === 'string' ? true : false;
    const endDate = !isIsoString ? new Date(d.end * 1000) : new Date(d.end);
    const startDate = !isIsoString ? new Date(d.start * 1000) : new Date(d.start);
    const baseWidth = this._xBaseScale(endDate) - this._xBaseScale(startDate);
    return k * baseWidth;
  }

  private _calcTimelineHeight(): number {
    return this._height - 40;
  }

  private _calcTimelineOffsetY(): number {
    return 0;
  }

  private _update(): void {
    const segmentData = this._prepareSegmentData();

    // BIND: Bind data with key function for object constancy
    const segments = this._progressElement
      .selectAll('.timeline--segment')
      .data(segmentData, (d: any) => d.id);

    // EXIT: Remove segments that no longer exist in data
    segments.exit().remove();

    // ENTER: Create new segments for new data points
    const enterSegments = segments
      .enter()
      .append('rect')
      .attr('class', 'timeline--segment')
      .attr('data-start', (d) => d.start)
      .attr('data-end', (d) => d.end)
      .attr('data-path', (d) => d.file_path || null)
      .attr('y', 0)
      .attr('height', this._timelineHeightFn)
      .on('click', () => this._onClick());

    // UPDATE: Update both existing and new segments
    segments
      .merge(enterSegments)
      .attr('x', (d) => {
        const baseX = this._getSegmentXPosition(d);
        return this.transformEvent ? this.transformEvent.applyX(baseX) : baseX;
      })
      .attr('width', (d) => {
        const k = this.transformEvent?.k || 1;
        return this._getSegmentWidth(d, k);
      });
  }

  private _updateMarkerPosition(): void {
    this._marker
      .attr('x1', this._xScale(this._currentTime))
      .attr('y1', 0)
      .attr('x2', this._xScale(this._currentTime))
      .attr('y2', this._timelineHeightFn);

    if (this._markerHitArea) {
      this._markerHitArea.attr('x', this._xScale(this._currentTime) - MARKER_HIT_AREA_OFFSET);
    }
  }

  private _onMouseEnter(): void {
    if (!this._fullscreen) {
      return;
    }
    
    const mouseEvent = d3.event as MouseEvent;
    const mouseX = mouseEvent.offsetX;
    const hoverTime = this._xScale.invert(mouseX);
    
    if (hoverTime > this._liveTime) {
      this._hoverMarker.style('opacity', 0);
      if (this._tooltipHost) {
        this._tooltipHost.hideTooltip();
      }
      return;
    }
    
    this._hoverMarker.style('opacity', 0.45);
  }

  private _onMouseMove(): void {
    if (!this._fullscreen) {
      return;
    }
    
    const mouseEvent = d3.event as MouseEvent;
    const mouseX = mouseEvent.offsetX;
    const hoverTime = this._xScale.invert(mouseX);
    
    if (hoverTime > this._liveTime) {
      this._hoverMarker.style('opacity', 0);
      if (this._tooltipHost) {
        this._tooltipHost.hideTooltip();
      }
      return;
    }
    
    this._hoverMarker
      .attr('x1', mouseX)
      .attr('x2', mouseX)
      .style('opacity', 0.45);

    if (this._tooltipHost) {
      const timeString = hoverTime.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
      this._tooltipHost.updateTooltip(mouseEvent, timeString);
    }
  }

  private _onMouseLeave(): void {
    if (!this._fullscreen) {
      return;
    }
    
    this._hoverMarker.style('opacity', 0);
    
    if (this._tooltipHost) {
      this._tooltipHost.hideTooltip();
    }
  }
}
